import type { ErrorInfo } from 'react';
import type { GatsbyBrowser } from 'gatsby';
import isBrowserFirefox from './src/utils/isBrowserFirefox';
import ignoreDevUseIdHydrationWarnings from './src/utils/ignoreDevUseIdHydrationWarnings';
import { sentryReactException } from './src/utils/sentry';
import { wrapPageElementWithErrorBoundary } from './src/components/SiteErrorBoundary';

// Also in gatsby-ssr, so the server and browser render the same tree
export const wrapPageElement: GatsbyBrowser['wrapPageElement'] =
    wrapPageElementWithErrorBoundary;

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
    if (process.env.NODE_ENV === 'development') {
        ignoreDevUseIdHydrationWarnings();
    }
};

// Gatsby's types don't include these yet
interface ReactErrorArgs {
    error: unknown;
    errorInfo: ErrorInfo;
}

/**
 * Errors caught by an error boundary. React doesn't report these anywhere
 * else, so Sentry wouldn't see them.
 */
export const onCaughtError = ({ error, errorInfo }: ReactErrorArgs): void => {
    sentryReactException(error, errorInfo);
};

/**
 * Errors no error boundary caught, which unmount the whole app. Gatsby logs
 * these to the console instead of reporting them as uncaught errors, so
 * Sentry's global handlers wouldn't see them either.
 */
export const onUncaughtError = ({ error, errorInfo }: ReactErrorArgs): void => {
    sentryReactException(error, errorInfo);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
    routerProps: { location },
    prevRouterProps,
    getSavedScrollPosition,
}) => {
    if (!prevRouterProps || location.hash || !isBrowserFirefox()) {
        return true;
    }

    /*
    BUG WORKAROUND
    There is an issue with Firefox not always scrolling to the top of the page when navigating...
     */
    const currentPosition = getSavedScrollPosition(location);
    setTimeout(() => {
        window.scrollTo({
            top: currentPosition[0] || 0,
            left: currentPosition[1] || 0,
        });
    }, 0);
    return false;
};
